/**
 * Componente principal de la aplicación BeautyBook
 * 
 * Configuración:
 * - React Query: Para manejo de estado del servidor y caché
 * - React Router: Para navegación entre páginas
 * - Toaster: Para notificaciones tipo toast
 * - TooltipProvider: Para tooltips de shadcn/ui
 * 
 * Estructura de rutas:
 * - /: Página de inicio (Home)
 * - /profile: Perfil del usuario (protegida)
 * - /login: Inicio de sesión
 * - /register: Registro de usuario
 * - *: Página 404 para rutas no encontradas
 */

import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useAuth } from "./hooks/useAuth";
import { Layout } from "./components/layout/Layout";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { ProviderRoute } from "./components/auth/ProviderRoute";
import { OwnerPinGate } from "./components/auth/OwnerPinGate";
import { Loader2 } from "lucide-react";

// Páginas críticas que se cargan inmediatamente (first paint)
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Lazy loading para páginas que se cargan bajo demanda
const Profile = lazy(() => import("./pages/Profile"));
const SubscriptionPlans = lazy(() => import("./pages/SubscriptionPlans"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const ProviderDashboard = lazy(() => import("./pages/ProviderDashboard"));
const ProviderMessagesPage = lazy(() => import("./pages/ProviderMessagesPage"));
const Services = lazy(() => import("./pages/Services"));
const ServiceForm = lazy(() => import("./pages/ServiceForm"));
const Bookings = lazy(() => import("./pages/Bookings"));
const BookService = lazy(() => import("./pages/BookService"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const ServicesCatalog = lazy(() => import("./pages/ServicesCatalog"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Workers = lazy(() => import("./pages/Workers"));
const WorkerForm = lazy(() => import("./pages/WorkerForm"));
const WorkerCalendar = lazy(() => import("./pages/WorkerCalendar"));
const CalendarView = lazy(() => import("./pages/Calendar"));
const Health = lazy(() => import("./pages/Health"));
const InventoryPage = lazy(() => import("./pages/InventoryPage"));
const SuppliersPage = lazy(() => import("./pages/SuppliersPage"));
const PaymentsPage = lazy(() => import("./pages/PaymentsPage"));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const ThemeSettingsPage = lazy(() => import("./pages/ThemeSettingsPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const NewBooking = lazy(() => import("./pages/NewBooking"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const LoyaltyPage = lazy(() => import("./pages/LoyaltyPage"));
const EmailTemplatesPage = lazy(() => import("./pages/EmailTemplatesPage"));
const NotificationsSettingsPage = lazy(() => import("./pages/NotificationsSettingsPage"));
const CancellationPolicyPage = lazy(() => import("./pages/CancellationPolicyPage"));
const AvailabilitySettingsPage = lazy(() => import("./pages/AvailabilitySettingsPage"));
const DepositSettingsPage = lazy(() => import("./pages/DepositSettingsPage"));
const SubscriptionSettingsPage = lazy(() => import("./pages/SubscriptionSettingsPage"));
const TimeClockPage = lazy(() => import("./pages/TimeClockPage"));
const SalesPage = lazy(() => import("./pages/SalesPage"));
const ProviderOnboarding = lazy(() => import("./pages/ProviderOnboarding"));
const ProvidersDirectory = lazy(() => import("./pages/ProvidersDirectory"));
const PublicProviderProfile = lazy(() => import("./pages/PublicProviderProfile"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const WebhooksDocumentation = lazy(() => import("./pages/WebhooksDocumentation"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccountPage"));
const ServicesClientsPage = lazy(() => import("./pages/ServicesClientsPage"));
const ServicesProfessionalsPage = lazy(() => import("./pages/ServicesProfessionalsPage"));
const AICampaignsPage = lazy(() => import("./pages/AICampaignsPage"));
const AIAgentsPage = lazy(() => import("./pages/AIAgentsPage"));
const MarketingPage = lazy(() => import("./pages/MarketingPage"));
const PortfolioManager = lazy(() => import("./pages/PortfolioManager"));
const GoogleCalendarCallback = lazy(() => import("./pages/GoogleCalendarCallback"));
const GoogleCalendarSettingsPage = lazy(() => import("./pages/GoogleCalendarSettingsPage"));
const KioskMode = lazy(() => import("./pages/KioskMode"));
const ConsentPage = lazy(() => import("./pages/ConsentPage"));
const TermsAdminPage = lazy(() => import("./pages/TermsAdminPage"));
const MyTimesheet = lazy(() => import("./pages/MyTimesheet"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const ClientFavorites = lazy(() => import("./pages/ClientFavorites"));
const BookingDetail = lazy(() => import("./pages/BookingDetail"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));

// Job Board Pages
const JobLandingPage = lazy(() => import("./pages/JobLandingPage"));
const CandidateRegistration = lazy(() => import("./pages/CandidateRegistration"));
const CandidateLogin = lazy(() => import("./pages/CandidateLogin"));
const CandidateDashboard = lazy(() => import("./pages/CandidateDashboard"));
const CandidatesSearch = lazy(() => import("./pages/public/CandidatesSearch"));
const JobCreditsPage = lazy(() => import("./pages/JobCreditsPage"));

// Admin Pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProviders = lazy(() => import("./pages/admin/AdminProviders"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const AdminFeatures = lazy(() => import("./pages/admin/AdminFeatures"));
const AdminAIAgents = lazy(() => import("./pages/admin/AdminAIAgents"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
import { AdminRoute } from "./components/admin/AdminRoute";

// Componente de loading para las rutas lazy
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Wrapper para lazy routes con Suspense
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

// Cliente de React Query para manejo de estado del servidor con configuración optimizada
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Datos considerados frescos por 2 minutos (evita refetch innecesarios)
      staleTime: 2 * 60 * 1000,
      // Datos en caché por 5 minutos antes de ser eliminados
      gcTime: 5 * 60 * 1000, // gcTime reemplaza a cacheTime en React Query v5
      // Retry fallido solo 1 vez (por defecto es 3)
      retry: 1,
      // Refetch en background cuando la ventana recupera el foco
      refetchOnWindowFocus: false,
      // Refetch cuando se reconecta la red
      refetchOnReconnect: true,
    },
  },
});

// Wrapper: redirect authenticated users to their dashboard, show Home to guests
function HomeOrDashboard() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user?.user_metadata?.user_type === 'client') {
    return <Navigate to="/client-dashboard" replace />;
  }
  if (user?.user_metadata?.user_type === 'provider') {
    return <Navigate to="/dashboard" replace />;
  }
  return <Home />;
}

/**
 * Configuración de rutas de la aplicación
 * 
 * Rutas públicas:
 * - / (Home): Página principal con hero, búsqueda y categorías
 * - /login: Formulario de inicio de sesión
 * - /register: Formulario de registro con tipo de usuario
 * 
 * Rutas protegidas (requieren autenticación):
 * - /profile: Página de perfil del usuario
 */
const router = createBrowserRouter([
  // ===================================================
  // Rutas públicas con Layout general (navbar, footer)
  // ===================================================
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <HomeOrDashboard /> },
      { path: 'catalog', element: <LazyRoute><ServicesCatalog /></LazyRoute> },
      { path: 'search', element: <LazyRoute><SearchResults /></LazyRoute> },
      { path: 'subscription-plans', element: <LazyRoute><SubscriptionPlans /></LazyRoute> },
      { path: 'subscription-success', element: <LazyRoute><SubscriptionSuccess /></LazyRoute> },
      { path: 'health', element: <LazyRoute><Health /></LazyRoute> },
      { path: 'reservas', element: <LazyRoute><NewBooking /></LazyRoute> },
      {
        path: 'onboarding',
        element: (
          <ProtectedRoute>
            <LazyRoute><ProviderOnboarding /></LazyRoute>
          </ProtectedRoute>
        )
      },
      {
        path: 'profile',
        element: (
          <ProtectedRoute>
            <LazyRoute><Profile /></LazyRoute>
          </ProtectedRoute>
        )
      },
      {
        path: 'my-bookings',
        element: (
          <ProtectedRoute>
            <LazyRoute><MyBookings /></LazyRoute>
          </ProtectedRoute>
        )
      },
      {
        path: 'client-dashboard',
        element: (
          <ProtectedRoute>
            <LazyRoute><ClientDashboard /></LazyRoute>
          </ProtectedRoute>
        )
      },
      {
        path: 'client/favorites',
        element: (
          <ProtectedRoute>
            <LazyRoute><ClientFavorites /></LazyRoute>
          </ProtectedRoute>
        )
      },
      {
        path: 'book/:serviceId',
        element: <LazyRoute><BookService /></LazyRoute>
      },
      { path: 'providers', element: <LazyRoute><ProvidersDirectory /></LazyRoute> },
      { path: 'provider/:id', element: <LazyRoute><PublicProviderProfile /></LazyRoute> },
      { path: 'booking/:id', element: <LazyRoute><BookingDetail /></LazyRoute> },
      { path: 'review/:bookingId', element: <LazyRoute><ReviewPage /></LazyRoute> },
      { path: 'category/:slug', element: <LazyRoute><CategoryPage /></LazyRoute> },
      { path: 'about', element: <LazyRoute><AboutPage /></LazyRoute> },
      { path: 'contact', element: <LazyRoute><ContactPage /></LazyRoute> },
      { path: 'blog', element: <LazyRoute><BlogPage /></LazyRoute> },
      { path: 'faq', element: <LazyRoute><FAQPage /></LazyRoute> },
      { path: 'terms', element: <LazyRoute><TermsPage /></LazyRoute> },
      { path: 'privacy', element: <LazyRoute><PrivacyPage /></LazyRoute> },
      { path: 'cookies', element: <LazyRoute><CookiesPage /></LazyRoute> },
      { path: 'delete-account', element: <LazyRoute><DeleteAccountPage /></LazyRoute> },
      { path: 'services/clients', element: <LazyRoute><ServicesClientsPage /></LazyRoute> },
      { path: 'services/professionals', element: <LazyRoute><ServicesProfessionalsPage /></LazyRoute> },
    ],
  },

  // ===================================================
  // Rutas del Dashboard con DashboardLayout (sidebar fijo)
  // Solo accesible para providers — clients redirigidos a /client-dashboard
  // ===================================================
  {
    element: (
      <ProviderRoute>
        <DashboardLayout />
      </ProviderRoute>
    ),
    children: [
      { path: 'dashboard', element: <LazyRoute><ProviderDashboard /></LazyRoute> },
      { path: 'services', element: <LazyRoute><Services /></LazyRoute> },
      { path: 'services/new', element: <LazyRoute><ServiceForm /></LazyRoute> },
      { path: 'services/edit/:id', element: <LazyRoute><ServiceForm /></LazyRoute> },
      { path: 'bookings', element: <LazyRoute><Bookings /></LazyRoute> },
      { path: 'workers', element: <LazyRoute><Workers /></LazyRoute> },
      { path: 'workers/new', element: <LazyRoute><WorkerForm /></LazyRoute> },
      { path: 'workers/edit/:id', element: <LazyRoute><WorkerForm /></LazyRoute> },
      { path: 'workers/:workerId/calendar', element: <LazyRoute><WorkerCalendar /></LazyRoute> },
      { path: 'calendar', element: <LazyRoute><CalendarView /></LazyRoute> },
      { path: 'inventory', element: <LazyRoute><InventoryPage /></LazyRoute> },
      { path: 'suppliers', element: <LazyRoute><SuppliersPage /></LazyRoute> },
      { path: 'payments', element: <OwnerPinGate><LazyRoute><PaymentsPage /></LazyRoute></OwnerPinGate> },
      { path: 'invoices', element: <OwnerPinGate><LazyRoute><InvoicesPage /></LazyRoute></OwnerPinGate> },
      { path: 'settings', element: <OwnerPinGate><LazyRoute><SettingsPage /></LazyRoute></OwnerPinGate> },
      { path: 'theme-settings', element: <LazyRoute><ThemeSettingsPage /></LazyRoute> },
      { path: 'custom-dashboard', element: <LazyRoute><DashboardPage /></LazyRoute> },
      { path: 'clients', element: <OwnerPinGate><LazyRoute><ClientsPage /></LazyRoute></OwnerPinGate> },
      { path: 'analytics', element: <OwnerPinGate><LazyRoute><AnalyticsPage /></LazyRoute></OwnerPinGate> },
      { path: 'loyalty', element: <LazyRoute><LoyaltyPage /></LazyRoute> },
      { path: 'settings/notifications', element: <LazyRoute><NotificationsSettingsPage /></LazyRoute> },
      { path: 'settings/cancellation-policy', element: <LazyRoute><CancellationPolicyPage /></LazyRoute> },
      { path: 'settings/availability', element: <LazyRoute><AvailabilitySettingsPage /></LazyRoute> },
      { path: 'settings/deposits', element: <LazyRoute><DepositSettingsPage /></LazyRoute> },
      { path: 'settings/email-templates', element: <LazyRoute><EmailTemplatesPage /></LazyRoute> },
      { path: 'settings/webhooks', element: <LazyRoute><WebhooksDocumentation /></LazyRoute> },
      { path: 'settings/subscription', element: <OwnerPinGate><LazyRoute><SubscriptionSettingsPage /></LazyRoute></OwnerPinGate> },
      { path: 'settings/google-callback', element: <LazyRoute><GoogleCalendarCallback /></LazyRoute> },
      { path: 'settings/google-calendar', element: <LazyRoute><GoogleCalendarSettingsPage /></LazyRoute> },
      { path: 'worker-earnings', element: <Navigate to="/workers?tab=commissions" replace /> },
      { path: 'time-clock', element: <LazyRoute><TimeClockPage /></LazyRoute> },
      { path: 'sales', element: <LazyRoute><SalesPage /></LazyRoute> },
      { path: 'ai-campaigns', element: <LazyRoute><AICampaignsPage /></LazyRoute> },
      { path: 'ai-agents', element: <LazyRoute><AIAgentsPage /></LazyRoute> },
      { path: 'marketing', element: <LazyRoute><MarketingPage /></LazyRoute> },
      { path: 'portfolio', element: <LazyRoute><PortfolioManager /></LazyRoute> },
      { path: 'support', element: <LazyRoute><SupportPage /></LazyRoute> },
      { path: 'candidate-messages', element: <LazyRoute><ProviderMessagesPage /></LazyRoute> },
      { path: 'configuracion/terminos', element: <LazyRoute><TermsAdminPage /></LazyRoute> },
    ],
  },
  // ===================================================
  // Rutas de Administración (solo superadmin)
  // ===================================================
  {
    path: 'admin',
    element: (
      <AdminRoute>
        <LazyRoute><AdminDashboard /></LazyRoute>
      </AdminRoute>
    ),
  },
  {
    path: 'admin/providers',
    element: (
      <AdminRoute>
        <LazyRoute><AdminProviders /></LazyRoute>
      </AdminRoute>
    ),
  },
  {
    path: 'admin/plans',
    element: (
      <AdminRoute>
        <LazyRoute><AdminPlans /></LazyRoute>
      </AdminRoute>
    ),
  },
  {
    path: 'admin/features',
    element: (
      <AdminRoute>
        <LazyRoute><AdminFeatures /></LazyRoute>
      </AdminRoute>
    ),
  },
  {
    path: 'admin/ai-agents',
    element: (
      <AdminRoute>
        <LazyRoute><AdminAIAgents /></LazyRoute>
      </AdminRoute>
    ),
  },
  {
    path: 'admin/payments',
    element: (
      <AdminRoute>
        <LazyRoute><AdminPayments /></LazyRoute>
      </AdminRoute>
    ),
  },
  {
    path: 'admin/support',
    element: (
      <AdminRoute>
        <LazyRoute><AdminSupport /></LazyRoute>
      </AdminRoute>
    ),
  },
  {
    path: 'admin/team',
    element: (
      <AdminRoute>
        <LazyRoute><AdminTeam /></LazyRoute>
      </AdminRoute>
    ),
  },

  // ===================================================
  // Rutas de Bolsa de Empleo (públicas y protegidas)
  // ===================================================
  {
    path: 'empleo',
    element: <LazyRoute><JobLandingPage /></LazyRoute>,
  },
  {
    path: 'empleo/registro',
    element: <LazyRoute><CandidateRegistration /></LazyRoute>,
  },
  {
    path: 'empleo/login',
    element: <LazyRoute><CandidateLogin /></LazyRoute>,
  },
  {
    path: 'empleo/mi-perfil',
    element: <LazyRoute><CandidateDashboard /></LazyRoute>,
  },
  {
    path: 'empleo/candidatos',
    element: <LazyRoute><CandidatesSearch /></LazyRoute>,
  },
  {
    path: 'empleo/creditos',
    element: (
      <ProtectedRoute>
        <LazyRoute><JobCreditsPage /></LazyRoute>
      </ProtectedRoute>
    ),
  },

  // Rutas de autenticación (sin Layout)
  { path: 'login', element: <Login /> },
  { path: 'register', element: <Register /> },
  { path: 'forgot-password', element: <ForgotPasswordPage /> },
  { path: 'reset-password', element: <ResetPasswordPage /> },

  // Modo Kiosko (pantalla completa sin navbar/sidebar)
  { path: 'kiosk', element: <LazyRoute><KioskMode /></LazyRoute> },

  // Consentimiento RGPD/LOPD público (QR del salón) — sin navbar/sidebar
  { path: 'terminos/:slug', element: <LazyRoute><ConsentPage /></LazyRoute> },

  // Portal de fichajes del trabajador (público, auth por PIN)
  { path: 'my-timesheet', element: <LazyRoute><MyTimesheet /></LazyRoute> },

  // Ruta catch-all para páginas no encontradas
  { path: '*', element: <LazyRoute><NotFound /></LazyRoute> },
]);

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {/* Toast notifications para feedback al usuario */}
          <Toaster />
          <Sonner />

          {/* Router principal de la aplicación */}
          <RouterProvider router={router} />
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
